import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { TemplateSummary, Template, CANVAS_PRESETS, CanvasPreset, TemplateColorMode } from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';
import { Card, CardTitle } from '../components/Card';
import { Input, InputWrapper, Label } from '../components/Input';
import { renderComposite } from '../utils/canvasRenderer';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const TemplatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

const TemplateCard = styled(Card)`
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const TemplatePreviewWrapper = styled.div<{ $width: number; $height: number }>`
  /* Hero preview: dominant card visual. Fixed canvas-area height so the
     template thumbnail breathes and is the focal point of the card. The
     canvas inside contains itself with object-fit semantics via max-w/h. */
  width: calc(100% + ${({ theme }) => theme.spacing.xl} * 2);
  margin-left: -${({ theme }) => theme.spacing.xl};
  margin-right: -${({ theme }) => theme.spacing.xl};
  margin-top: -${({ theme }) => theme.spacing.xl};
  height: 380px;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  canvas {
    max-width: calc(100% - 36px);
    max-height: calc(100% - 36px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-radius: 4px;
  }
`;

const PreviewLoading = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

// Sample pastel colors for preview backgrounds
const PREVIEW_BACKGROUNDS = [
  'pastel:#E6E6FA', // Lavender
  'pastel:#FFE4E1', // Rose Quartz
  'pastel:#B4D4E7', // Serenity Blue
  'pastel:#C5E8D5', // Mint Green
  'pastel:#FFDAB9', // Peach
  'pastel:#D4E9F7', // Baby Blue
];

function TemplateThumbnail({ templateId, workspaceId, canvasWidth, canvasHeight }: {
  templateId: string;
  workspaceId: string;
  canvasWidth: number;
  canvasHeight: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const renderThumbnail = useCallback(async () => {
    try {
      const template = await apiClient.get<Template>(
        `/api/workspaces/${workspaceId}/templates/${templateId}`
      );

      if (!canvasRef.current) return;

      // Pick a consistent background color based on template ID hash
      const hash = templateId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
      const bgColor = PREVIEW_BACKGROUNDS[hash % PREVIEW_BACKGROUNDS.length];

      // Render at hero-preview resolution. The card preview area is ~380px on
      // its longer axis; render to that target so the thumbnail stays sharp
      // when scaled into the card without forcing the browser to upscale.
      const scale = 420 / Math.max(canvasWidth, canvasHeight);
      const rendered = await renderComposite(bgColor, template, { scale });

      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) return;

      canvasRef.current.width = rendered.width;
      canvasRef.current.height = rendered.height;
      ctx.drawImage(rendered, 0, 0);
      setLoading(false);
    } catch {
      setError(true);
      setLoading(false);
    }
  }, [templateId, workspaceId, canvasWidth, canvasHeight]);

  useEffect(() => {
    renderThumbnail();
  }, [renderThumbnail]);

  return (
    <TemplatePreviewWrapper $width={canvasWidth} $height={canvasHeight}>
      {loading && !error && <PreviewLoading>Loading...</PreviewLoading>}
      {error && <PreviewLoading>{canvasWidth} × {canvasHeight}</PreviewLoading>}
      <canvas ref={canvasRef} style={{ display: loading || error ? 'none' : 'block' }} />
    </TemplatePreviewWrapper>
  );
}

const TemplateInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const DefaultBadge = styled.span`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 2px 8px;
  border: ${({ theme }) => theme.borders.thin};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const GlobalBadge = styled.span`
  background: ${({ theme }) => theme.colors.secondary};
  color: white;
  padding: 2px 8px;
  border: ${({ theme }) => theme.borders.thin};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg};
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.medium};
  padding: ${({ theme }) => theme.spacing.xl};
  width: 100%;
  max-width: 450px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const ModalTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const PresetSelect = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: ${({ theme }) => theme.borders.medium};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`;

const ActionButton = styled.button<{ $variant?: 'delete' | 'duplicate' | 'toggle' }>`
  position: absolute;
  top: 12px;
  width: 28px;
  height: 28px;
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textMuted};
  border: ${({ theme }) => theme.borders.thin};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: transform 0.2s ease;
  z-index: 10;
  right: ${({ $variant }) =>
    $variant === 'toggle' ? '84px' : $variant === 'duplicate' ? '48px' : '12px'};

  &:hover {
    background-color: ${({ theme, $variant }) =>
      $variant === 'duplicate' || $variant === 'toggle' ? theme.colors.primary : theme.colors.error};
    color: white;
    transform: scale(1.1);
  }

  ${TemplateCard}:hover & {
    opacity: 1;
  }
`;

const DeleteButton = styled(ActionButton).attrs({ $variant: 'delete' as const })``;

const DuplicateButton = styled(ActionButton).attrs({ $variant: 'duplicate' as const })`
  font-size: 14px;
`;

const ToggleButton = styled(ActionButton).attrs({ $variant: 'toggle' as const })`
  font-size: 14px;
`;

const InactiveOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 5;
`;

const DisabledBadge = styled.span`
  position: absolute;
  top: 8px;
  left: 8px;
  background: ${({ theme }) => theme.colors.textMuted};
  color: white;
  padding: 2px 8px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  z-index: 6;
  border-radius: 2px;
`;

const ColorModeBadge = styled.span<{ $mode: TemplateColorMode }>`
  background: ${({ $mode }) =>
    $mode === 'brand' ? '#3B82F6' : $mode === 'adaptive' ? '#8B5CF6' : 'transparent'};
  color: white;
  padding: 2px 8px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  border-radius: 2px;
`;

const ColorModeSelect = styled.select`
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 11px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  margin-top: 8px;
`;

const SourceToggles = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 6px;
`;

const SourceChip = styled.button<{ $active: boolean; $color: string }>`
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid ${({ $color }) => $color};
  background: ${({ $active, $color }) => $active ? $color : 'transparent'};
  color: ${({ $active, $color }) => $active ? '#fff' : $color};
  opacity: ${({ $active }) => $active ? 1 : 0.5};
  transition: all 0.15s;
  &:hover { opacity: 1; }
`;

const SOURCE_CHIP_COLORS: Record<string, string> = {
  stock: '#3B82F6',
  pastel: '#A855F7',
  ai: '#F59E0B',
  asset: '#10B981',
};

const SOURCE_LABELS: Record<string, string> = {
  stock: 'Stock',
  pastel: 'Color',
  ai: 'AI',
  asset: 'Custom',
};

const ALL_SOURCES = ['stock', 'pastel', 'ai', 'asset'];

const CustomizeBtn = styled.button`
  width: 100%;
  padding: 6px;
  margin-top: 6px;
  border: 1.5px dashed ${({ theme }) => theme.colors.primary};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
  }
`;

const CardWrapper = styled.div<{ $inactive?: boolean }>`
  position: relative;
  opacity: ${({ $inactive }) => ($inactive ? 0.5 : 1)};
  transition: opacity 0.2s ease;
`;

const ClampedDescription = styled.p`
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 4px 0 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export function TemplatesPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<CanvasPreset>('instagram_square');
  const [isCreating, setIsCreating] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [isGlobalNew, setIsGlobalNew] = useState(false);

  useEffect(() => {
    if (workspaceId) {
      fetchTemplates();
    }
  }, [workspaceId]);

  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      const data = await apiClient.get<TemplateSummary[]>(`/api/workspaces/${workspaceId}/templates`);
      setTemplates(data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTemplateName.trim()) return;

    setIsCreating(true);
    try {
      const preset = CANVAS_PRESETS[selectedPreset];
      const template = await apiClient.post<TemplateSummary>(`/api/workspaces/${workspaceId}/templates`, {
        name: newTemplateName,
        canvasWidth: preset.width,
        canvasHeight: preset.height,
        isGlobal: isGlobalNew,
      });
      setTemplates([template, ...templates]);
      setIsModalOpen(false);
      setNewTemplateName('');
      setIsGlobalNew(false);
      // Navigate to editor
      navigate(`/workspace/${workspaceId}/templates/${template.id}`);
    } catch (error) {
      console.error('Failed to create template:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteTemplate = async (e: React.MouseEvent, template: TemplateSummary) => {
    e.stopPropagation();
    if (template.isGlobal && !user?.isAdmin) return;
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      await apiClient.delete(`/api/workspaces/${workspaceId}/templates/${template.id}`);
      setTemplates(templates.filter((t) => t.id !== template.id));
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const handleDuplicateTemplate = async (e: React.MouseEvent, templateId: string) => {
    e.stopPropagation();
    setDuplicatingId(templateId);

    try {
      const duplicated = await apiClient.post<TemplateSummary>(
        `/api/workspaces/${workspaceId}/templates/${templateId}/duplicate`,
        {}
      );
      setTemplates([duplicated, ...templates]);
    } catch (error) {
      console.error('Failed to duplicate template:', error);
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleToggleActive = async (e: React.MouseEvent, templateId: string) => {
    e.stopPropagation();
    try {
      await apiClient.patch(`/api/workspaces/${workspaceId}/templates/${templateId}/toggle-active`, {});
      fetchTemplates();
    } catch (err) {
      console.error('Failed to toggle template:', err);
    }
  };

  if (isLoading) {
    return <div>Loading templates...</div>;
  }

  return (
    <>
      <PageHeader>
        <div>
          <Title>Templates</Title>
          <Subtitle>Create overlay templates to brand your generated content</Subtitle>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button $variant="outline" onClick={() => navigate(`/workspace/${workspaceId}`)}>
            Back to Workspace
          </Button>
          <Button onClick={() => setIsModalOpen(true)}>+ New Template</Button>
        </div>
      </PageHeader>

      {templates.length === 0 ? (
        <EmptyState>
          <p>No templates yet. Create your first template to add brand overlays to your posts!</p>
          <Button onClick={() => setIsModalOpen(true)} style={{ marginTop: 16 }}>
            Create Template
          </Button>
        </EmptyState>
      ) : (
        <TemplatesGrid>
          {templates.map((template) => {
            const isInactive = template.isActive === false;
            return (
              <CardWrapper key={template.id} $inactive={isInactive}>
                {isInactive && <DisabledBadge>Disabled</DisabledBadge>}
                <TemplateCard onClick={() => navigate(`/workspace/${workspaceId}/templates/${template.id}`)}>
                  <ToggleButton
                    onClick={(e) => handleToggleActive(e, template.id)}
                    title={isInactive ? 'Enable template' : 'Disable template'}
                  >
                    {isInactive ? '○' : '●'}
                  </ToggleButton>
                  <DuplicateButton
                    onClick={(e) => handleDuplicateTemplate(e, template.id)}
                    disabled={duplicatingId === template.id}
                    title="Duplicate template"
                  >
                    {duplicatingId === template.id ? '...' : '⧉'}
                  </DuplicateButton>
                  {(!template.isGlobal || user?.isAdmin) && (
                    <DeleteButton onClick={(e) => handleDeleteTemplate(e, template)} title="Delete template">×</DeleteButton>
                  )}
                  {isInactive && <InactiveOverlay />}
                  <TemplateThumbnail
                    templateId={template.id}
                    workspaceId={workspaceId!}
                    canvasWidth={template.canvasWidth}
                    canvasHeight={template.canvasHeight}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <CardTitle style={{ fontSize: '15px', fontWeight: 700, lineHeight: 1.2 }}>{template.name}</CardTitle>
                      {template.description && (
                        <ClampedDescription>{template.description}</ClampedDescription>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                      {template.isGlobal && <GlobalBadge>Global</GlobalBadge>}
                      {template.isDefault && <DefaultBadge>Default</DefaultBadge>}
                      {template.colorMode && template.colorMode !== 'fixed' && (
                        <ColorModeBadge $mode={template.colorMode}>
                          {template.colorMode === 'brand' ? 'Brand' : 'Adaptive'}
                        </ColorModeBadge>
                      )}
                    </div>
                  </div>
                  <TemplateInfo>
                    <span>{template.elementCount} elements · {template.canvasWidth}×{template.canvasHeight}</span>
                  </TemplateInfo>
                  <ColorModeSelect
                    value={template.colorMode || 'fixed'}
                    onClick={(e) => e.stopPropagation()}
                    onChange={async (e) => {
                      e.stopPropagation();
                      try {
                        await apiClient.put(`/api/workspaces/${workspaceId}/templates/${template.id}`, {
                          colorMode: e.target.value,
                        });
                        fetchTemplates();
                      } catch { /* ignore */ }
                    }}
                  >
                    <option value="fixed">Fixed Colors</option>
                    <option value="brand">Brand Colors</option>
                    <option value="adaptive">Adaptive</option>
                  </ColorModeSelect>
                  <SourceToggles onClick={(e) => e.stopPropagation()}>
                    {ALL_SOURCES.map((source) => {
                      const suitable = template.suitableFor || 'all';
                      const activeSources = suitable === 'all' ? ALL_SOURCES : suitable.split(',').map(s => s.trim());
                      const isActive = activeSources.includes(source);
                      return (
                        <SourceChip
                          key={source}
                          $active={isActive}
                          $color={SOURCE_CHIP_COLORS[source]}
                          onClick={async () => {
                            let newSources: string[];
                            if (isActive) {
                              newSources = activeSources.filter(s => s !== source);
                              if (newSources.length === 0) return; // at least one must be active
                            } else {
                              newSources = [...activeSources, source];
                            }
                            const newValue = newSources.length === ALL_SOURCES.length ? 'all' : newSources.join(',');
                            try {
                              await apiClient.put(`/api/workspaces/${workspaceId}/templates/${template.id}`, {
                                suitableFor: newValue,
                              });
                              fetchTemplates();
                            } catch { /* ignore */ }
                          }}
                        >
                          {SOURCE_LABELS[source]}
                        </SourceChip>
                      );
                    })}
                  </SourceToggles>
                  {template.isGlobal && (
                    <CustomizeBtn
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const duplicated = await apiClient.post<{ id: string }>(
                            `/api/workspaces/${workspaceId}/templates/${template.id}/duplicate`,
                            {}
                          );
                          navigate(`/workspace/${workspaceId}/templates/${duplicated.id}`);
                        } catch (err) {
                          console.error('Failed to duplicate:', err);
                        }
                      }}
                    >
                      Customize Copy
                    </CustomizeBtn>
                  )}
                </TemplateCard>
              </CardWrapper>
            );
          })}
        </TemplatesGrid>
      )}

      {isModalOpen && (
        <Modal onClick={() => setIsModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Create New Template</ModalTitle>
            <form onSubmit={handleCreateTemplate}>
              <InputWrapper style={{ marginBottom: 16 }}>
                <Label htmlFor="templateName">Template Name</Label>
                <Input
                  id="templateName"
                  type="text"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="e.g., Logo Watermark"
                  required
                  autoFocus
                />
              </InputWrapper>
              <InputWrapper>
                <Label htmlFor="canvasPreset">Canvas Size</Label>
                <PresetSelect
                  id="canvasPreset"
                  value={selectedPreset}
                  onChange={(e) => setSelectedPreset(e.target.value as CanvasPreset)}
                >
                  {Object.entries(CANVAS_PRESETS).map(([key, preset]) => (
                    <option key={key} value={key}>
                      {preset.label} ({preset.width}×{preset.height})
                    </option>
                  ))}
                </PresetSelect>
              </InputWrapper>
              {user?.isAdmin && (
                <InputWrapper style={{ marginTop: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={isGlobalNew}
                      onChange={(e) => setIsGlobalNew(e.target.checked)}
                    />
                    <span>Create as Global Template (visible to all users)</span>
                  </label>
                </InputWrapper>
              )}
              <ModalActions>
                <Button type="button" $variant="outline" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isCreating || !newTemplateName.trim()}>
                  {isCreating ? 'Creating...' : 'Create & Edit'}
                </Button>
              </ModalActions>
            </form>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
